import React from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import commonActions from "../../redux/common/actions";
import { cssProperties } from "../../utils/commonCssProperties";

// Shared pieces of the Email screens, styled like the rest of the back office.

// The white card the screens sit in (as in Bulk Upload / Template Management).
export const cardSx = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  p: 3,
  minHeight: "50vh",
};

export const labelSx = { fontWeight: "bold", color: cssProperties?.fontcolor?.primary, mb: 0.5 };

// Toast + page loader through the common reducer.
export const useFeedback = () => {
  const dispatch = useDispatch();
  const alert = (status, message) => {
    dispatch({ type: commonActions.SET_ALERT, payload: { status, show: true, message } });
    setTimeout(() => dispatch({ type: commonActions.SET_ALERT, payload: { status: null, show: false, message: null } }), 3000);
  };
  return {
    success: (message) => alert("success", message),
    failed: (message) => alert("failed", message),
    loader: (on) => dispatch({ type: commonActions.SET_PAGE_LOADER, payload: on }),
  };
};

const headSx = {
  color: cssProperties?.color?.primary,
  borderBottom: `1px solid ${cssProperties?.bordercolor?.primary4blue}`,
  background: cssProperties?.backgroundcolor?.primary,
  fontSize: "15px",
  fontWeight: "500",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const bodySx = {
  color: "#212121",
  fontSize: "14px",
  borderBottom: `1px solid ${cssProperties?.bordercolor?.primary4blue}`,
  textAlign: "center",
  py: 1.5,
};

/**
 * columns: [{ name, accessor, align?, render?(row, index) }]
 */
export function EmailTable({ columns, rows, emptyText = "No records found", startIndex = 0 }) {
  return (
    <TableContainer sx={{ backgroundColor: "transparent", borderRadius: "8px", border: `1px solid ${cssProperties?.bordercolor?.primary4blue}` }}>
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.name} sx={{ ...headSx, textAlign: c.align || "center" }}>{c.name}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(!rows || rows.length === 0) && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ ...bodySx, py: 4, color: "#666" }}>{emptyText}</TableCell>
            </TableRow>
          )}
          {rows?.map((row, i) => (
            <TableRow key={row.key ?? i} hover>
              {columns.map((c) => (
                <TableCell key={c.name} sx={{ ...bodySx, textAlign: c.align || "center" }}>
                  {c.render ? c.render(row, startIndex + i) : row[c.accessor] ?? "—"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const STATUS_COLORS = {
  RUNNING: { bg: "rgba(63, 65, 209, 0.12)", fg: "rgba(63, 65, 209, 1)" },
  COMPLETED: { bg: "rgba(34, 160, 107, 0.12)", fg: "rgba(34, 160, 107, 1)" },
  SENT: { bg: "rgba(34, 160, 107, 0.12)", fg: "rgba(34, 160, 107, 1)" },
  delivered: { bg: "rgba(34, 160, 107, 0.12)", fg: "rgba(34, 160, 107, 1)" },
  open: { bg: "rgba(34, 160, 107, 0.12)", fg: "rgba(34, 160, 107, 1)" },
  click: { bg: "rgba(34, 160, 107, 0.12)", fg: "rgba(34, 160, 107, 1)" },
  SKIPPED: { bg: "rgba(237, 145, 33, 0.14)", fg: "rgba(181, 98, 0, 1)" },
  INTERRUPTED: { bg: "rgba(237, 145, 33, 0.14)", fg: "rgba(181, 98, 0, 1)" },
  deferred: { bg: "rgba(237, 145, 33, 0.14)", fg: "rgba(181, 98, 0, 1)" },
  processed: { bg: "rgba(63, 65, 209, 0.12)", fg: "rgba(63, 65, 209, 1)" },
  FAILED: { bg: "rgba(241, 91, 80, 0.14)", fg: "rgba(200, 40, 30, 1)" },
  bounce: { bg: "rgba(241, 91, 80, 0.14)", fg: "rgba(200, 40, 30, 1)" },
  dropped: { bg: "rgba(241, 91, 80, 0.14)", fg: "rgba(200, 40, 30, 1)" },
  spamreport: { bg: "rgba(241, 91, 80, 0.14)", fg: "rgba(200, 40, 30, 1)" },
};

// Any provider's status by meaning (SMS gateways name them differently: Delivered, DND, Undelivered ...).
const colorOf = (status) => {
  const s = String(status || "").toLowerCase();
  if (STATUS_COLORS[status]) return STATUS_COLORS[status];
  if (/fail|undeliv|dnd|expire|reject|block|invalid|bounce|drop/.test(s)) return STATUS_COLORS.FAILED;
  if (/deliver|read|open|click/.test(s)) return STATUS_COLORS.SENT;
  if (/pend|submit|sent|accept|queue|process/.test(s)) return STATUS_COLORS.RUNNING;
  return { bg: "rgba(0,0,0,0.06)", fg: "#333" };
};

export function StatusChip({ status }) {
  const c = colorOf(status);
  return <Chip size="small" label={status} sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600 }} />;
}

// A small figure tile: Sent 120
export function CountTile({ label, value, color }) {
  return (
    <Box sx={{ border: `1px solid ${cssProperties?.bordercolor?.primary4blue}`, borderRadius: "8px", px: 2, py: 1.5, minWidth: 120 }}>
      <Typography variant="caption" sx={{ color: "#666" }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: color || cssProperties?.fontcolor?.primary, fontWeight: 600 }}>{value ?? "—"}</Typography>
    </Box>
  );
}

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

// "{{name}}" for showing a variable.
export const token = (name) => `{{${name}}}`;
